import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActionLogSender } from '@framework/action-log';
import { Loading } from '@framework/ui/atoms';
import { Button } from '@framework/ui/atoms/Button';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import type { GroupId, UserId, WorkspaceId } from 'balus-common';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import { ViewModelOperation } from '@view-model/domain/view-model';
import { Workspace, WorkspaceMemberRole, WorkspaceMemberRoles } from '@workspace/domain/workspace';
import { useAssignedWorkspaces } from '@workspace/ui/WorkspaceList';
import { ObjectRepository } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import type { Folder } from '@workspace/view-model-folder/domain/Folder';

type WorkspaceData = { workspace: Workspace; roles: WorkspaceMemberRoles };

const fetchWorkspaceMembers = async (workspaces: Workspace[]): Promise<WorkspaceData[]> => {
  const result: WorkspaceData[] = [];

  await Promise.all(
    workspaces.map(async (workspace) => {
      const repo = new ObjectRepository(WorkspaceMemberRoles, RTDBPath.Workspace.memberRolesPath(workspace.id));
      const roles = (await repo.get()) || WorkspaceMemberRoles.load({});
      result.push({ workspace, roles });
    })
  );

  return result;
};

type Props = {
  isOpen: boolean;
  userId: UserId;
  groupId: GroupId;
  workspaceId: WorkspaceId;
  viewModel?: ViewModelEntity;
  folder?: Folder;
  onClose(): void;
};

export const MoveToOtherWorkspaceModal: React.FC<Props> = ({
  isOpen,
  userId,
  groupId,
  workspaceId,
  viewModel,
  folder,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const actionLogSender = useActionLogSender();
  const allWorkspaces = useAssignedWorkspaces(userId, groupId);

  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null);

  useEffect(() => {
    if (allWorkspaces === null || userId === null) {
      setWorkspaces(null);
      return;
    }

    fetchWorkspaceMembers(allWorkspaces).then((wsWithRoles) => {
      setWorkspaces(
        wsWithRoles
          .filter(({ workspace }) => workspace.isShared)
          .filter(({ roles }) => {
            const role = roles.getRoleOf(userId);
            return role && [WorkspaceMemberRole.admin, WorkspaceMemberRole.editor].includes(role);
          })
          .filter(({ workspace }) => workspace.id !== workspaceId)
          .map(({ workspace }) => workspace)
          .sort(Workspace.compare)
      );
    });
  }, [allWorkspaces, userId, workspaceId]);

  const [destinationWorkspaceId, setDestinationWorkspaceId] = useState<WorkspaceId | null>(null);
  useEffect(() => {
    setDestinationWorkspaceId(null);
  }, [isOpen]);

  const [processing, setProcessing] = useState<boolean>(false);
  const canMove = destinationWorkspaceId !== null;

  const handleClose = useCallback(() => onClose(), [onClose]);
  const handleMove = useCallback(async () => {
    if (destinationWorkspaceId === null) {
      alert(t('workspace.moveToOtherWorkspace.selectAlert'));
      return;
    }

    setProcessing(true);
    try {
      if (viewModel) {
        await ViewModelOperation.moveToOtherWorkspace(viewModel.id, destinationWorkspaceId, actionLogSender);
      }

      if (folder) {
        await ViewModelOperation.moveFolderToOtherWorkspace(folder.id, destinationWorkspaceId, actionLogSender);
      }

      setDestinationWorkspaceId(null);
      onClose();
    } finally {
      setProcessing(false);
    }
  }, [actionLogSender, destinationWorkspaceId, folder, onClose, viewModel, t]);

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        viewModel
          ? t('workspace.moveToOtherWorkspace.viewModelTitle')
          : folder
            ? t('workspace.moveToOtherWorkspace.folderTitle')
            : ''
      }
      submitButton={
        <Button onClick={handleMove} disabled={!canMove} loading={processing}>
          {t('workspace.modal.move')}
        </Button>
      }
      cancelButton={
        <Button onClick={handleClose} color={'secondary'}>
          {t('common.action.cancel')}
        </Button>
      }
    >
      <div className="p-2" style={{ width: '600px' }}>
        {viewModel && t('workspace.moveToOtherWorkspace.viewModelDescription', { name: viewModel.name.value })}
        {folder && t('workspace.moveToOtherWorkspace.folderDescription', { name: folder.name })}
        <br />
        <span className="text-sm">{t('workspace.moveToOtherWorkspace.reloadNote')}</span>
        <hr className="mt-2" />
        {workspaces === null ? (
          <Loading />
        ) : workspaces.length === 0 ? (
          t('workspace.moveToOtherWorkspace.emptyState')
        ) : (
          <div className="mb-4 mt-2 overflow-y-auto" style={{ minHeight: '250px', maxHeight: '50vh' }}>
            {workspaces.map((workspace) => (
              <div key={workspace.id}>
                <label className="block cursor-pointer rounded p-2 hover:bg-gray-200">
                  <input
                    type="radio"
                    name="destination-workspace"
                    className="form-radio mr-1 size-6 border-brand"
                    checked={workspace.id === destinationWorkspaceId}
                    onChange={() => setDestinationWorkspaceId(workspace.id)}
                    disabled={processing}
                  />
                  {workspace.name.toString()}
                </label>
              </div>
            ))}
          </div>
        )}
      </div>
    </GeneralModal>
  );
};
